import { SectionElementProps, ElementType, SectionProps } from '@jbhi-fi/components/src/Section/Section';
import { contentfulApiCardGridToCardGridWithProviderProps } from './CardGridTransformer';
import {
  ContentfulCardGrid,
  ContentfulCarousel,
  ContentfulContentBanner,
  ContentfulHeroBlock,
  ContentfulSection,
  ContentfulSubCategoryPage,
  ContentfulCarouselType,
  ContentfulListingBlock,
  ContentfulProductBlock,
  ContentfulMultiColumnBlock,
  ContentfulSideBySide,
  ContentfulProductGrid,
} from '@jbhi-fi/apis/src/contentful/types';
import { HeroBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/HeroBlockTransformer';
import { ContentBlockProps, ContentBlockStyleProps } from '@jbhi-fi/components/src/ContentBlock';
import { HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { determineCarouselTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CarouselTransformer/determineCarouselTransformer';
import { ThemeTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { ContentBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ContentBannerTransformer';
import { ListingBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ListingBlockTransformer';
import { ProductBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ProductBlockTransformer';
import { MultiColumnBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/MultiColumnBlockTransformer';
import { SideBySideTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SideBySideTransformer';
import { getContentEntryBlocks } from '@jbhi-fi/components/src/views/Homepage/entryContentBlockPropsMap';
import { ProductGridTransformer } from './ProductGridTransformer';

const sectionHasElementType = (section: ContentfulSection, elementType: string) =>
  section?.fields?.sectionContent?.findIndex(element => element?.sys?.contentType?.sys?.id === elementType) !== -1;

type CarouselTransformer = {
  transformer: (entity: any) => any;
};

export type ContentfulApiSectionToSectionPropsOptions = {
  contentBlockProps?: ContentBlockStyleProps;
  isShowcase?: boolean;
};

const wrapElement =
  <T extends { sys?: { contentType?: { sys?: { id?: string } } } }>(
    helper: (opts: { element: T }) => SectionElementProps<any> | undefined
  ) =>
  (entry: T): SectionProps | undefined => {
    if (!(entry as any)?.fields) return undefined;
    const elementProps = helper({ element: entry });
    const sectionProps: SectionProps = {
      elements: elementProps ? [elementProps] : [],
    };
    const contentBlockProps = getContentEntryBlocks({ isLoggedIn: true })[entry?.sys?.contentType?.sys?.id ?? ''];
    if (contentBlockProps) {
      sectionProps.contentBlockProps =
        typeof contentBlockProps === 'function' ? contentBlockProps(entry as any) : contentBlockProps;
    }
    return sectionProps;
  };

const ELEMENT_HANDLERS: Record<string, (el: any) => SectionProps | undefined> = {
  listingBlock: wrapElement(({ element }: { element: ContentfulListingBlock }) =>
    contentfulApiListingBlockToSectionElementProps({ listingBlock: element })
  ),
  productBlock: wrapElement(({ element }: { element: ContentfulProductBlock }) =>
    contentfulApiProductBlockToSectionElementProps({ productBlock: element })
  ),
  multiColumnBlock: wrapElement(({ element }: { element: ContentfulMultiColumnBlock }) =>
    contentfulApiMultiColumnBlockToSectionElementProps({ multiColumnBlock: element })
  ),
  sideBySide: wrapElement(({ element }: { element: ContentfulSideBySide }) =>
    contentfulApiSideBySideToSectionElementProps({ sideBySide: element })
  ),
  carousel: wrapElement(({ element }: { element: ContentfulCarousel }) => {
    const fieldType = element?.fields?.type;
    return fieldType === ContentfulCarouselType.PromoCard
      ? contentfulApiCarouselToSectionElementProps(element)
      : undefined;
  }),
};

export abstract class SectionTransformer {
  static toSectionProps = (
    section: ContentfulSection,
    options: ContentfulApiSectionToSectionPropsOptions = {}
  ): SectionProps | undefined => {
    const sectionElements = section?.fields?.sectionContent
      ?.map(element => getSectionElementProps({ element, section }))
      ?.filter(Boolean) as Array<SectionElementProps<any>>;
    const isURLEmpty = !section?.fields?.url;
    const baseSectionProps = getSectionPropsFromElementType(section, isURLEmpty);
    const shouldShowHeading = section.fields.headingStyle !== 'Hidden';
    return section?.fields && sectionElements?.length
      ? {
          href: section?.fields?.url,
          shouldOpenInNewWindow: section?.fields?.shouldOpenInNewWindow,
          title: shouldShowHeading ? (section.fields.heading ?? undefined) : undefined,
          subText: shouldShowHeading ? (section.fields.subText ?? undefined) : undefined,
          isShowcase: options.isShowcase ?? false,
          headingStyle: section.fields.headingStyle ?? 'Left',
          ...baseSectionProps,
          contentBlockProps: {
            ...(baseSectionProps.contentBlockProps ?? {}),
            ...(options.contentBlockProps ?? {}),
          },
          elements: sectionElements,
          ...(section.fields?.theme?.fields
            ? {
                contentPalette: ThemeTransformer.toContentThemeVariables(section.fields.theme).palette,
              }
            : {}),
        }
      : undefined;
  };

  static toSectionPropsFromContentHubHomePage = ({
    entry,
    isShowcase = false,
  }: {
    entry:
      | ContentfulSection
      | ContentfulListingBlock
      | ContentfulProductBlock
      | ContentfulMultiColumnBlock
      | ContentfulSideBySide;
    isShowcase: boolean;
  }): SectionProps | undefined => {
    const typeId = entry?.sys?.contentType?.sys?.id;
    if (
      typeId === 'section' &&
      entry?.fields &&
      'sectionContent' in entry.fields &&
      Array.isArray((entry.fields as { sectionContent?: Array<unknown> }).sectionContent)
    ) {
      return SectionTransformer.toSectionProps(entry as ContentfulSection, { isShowcase });
    }
    const handler = typeId && ELEMENT_HANDLERS[typeId];
    if (handler && typeId !== 'section') {
      return handler(
        entry as ContentfulListingBlock | ContentfulProductBlock | ContentfulMultiColumnBlock | ContentfulSideBySide
      );
    }
    return undefined;
  };

  static toContentBlockProps = (entry: ContentfulSection): Partial<ContentBlockProps> => {
    const themePalette = ThemeTransformer.toContentThemePalette(entry.fields?.theme);

    return {
      style: { backgroundColor: themePalette.background },
      paddingY: 7.5,
    };
  };
}

const getSectionPropsFromElementType = (
  section: ContentfulSection,
  isURLEmpty: boolean
): Omit<SectionProps, 'elements'> => {
  if (sectionHasElementType(section, 'carousel'))
    return getCarouselSectionProps(
      section?.fields?.sectionContent?.find(s => s.sys.contentType?.sys.id === 'carousel') as ContentfulCarousel
    );
  if (isURLEmpty && section.fields?.sectionContent?.length === 1 && sectionHasElementType(section, 'cardGrid'))
    return {
      href: getCardGridParentPageHref(
        section?.fields?.sectionContent?.find(s => s.sys.contentType?.sys.id === 'cardGrid') as ContentfulCardGrid
      ),
    };
  return {};
};

const getCarouselSectionProps = (carousel: ContentfulCarousel): Omit<SectionProps, 'elements'> => {
  switch (carousel.fields?.type) {
    case ContentfulCarouselType.Chart:
    case ContentfulCarouselType.Merch:
    case ContentfulCarouselType.Video:
      return {
        contentBlockProps: {
          paddingLeft: { mobile: 0, tablet: 'xlarge' },
          paddingRight: { mobile: 0, tablet: 'xlarge' },
        },
      };
    default:
      return {
        titleVariant: HeadingDividerVariant.small,
        titlePadding: 5,
      };
  }
};

const SECTION_ELEMENT_CONFIG: Record<
  string,
  (params: {
    element:
      | ContentfulCardGrid
      | ContentfulCarousel
      | ContentfulHeroBlock
      | ContentfulContentBanner
      | ContentfulListingBlock
      | ContentfulProductBlock
      | ContentfulMultiColumnBlock
      | ContentfulSideBySide
      | ContentfulProductGrid;
    section: ContentfulSection;
  }) => SectionElementProps<any> | undefined
> = {
  carousel: ({ element }) => contentfulApiCarouselToSectionElementProps(element as ContentfulCarousel),
  heroBlock: ({ element }) => contentfulApiHeroBlockToSectionElementProps(element as ContentfulHeroBlock),
  contentBanner: ({ element }) => contentfulApiContentBannerToSectionElementProps(element as ContentfulContentBanner),
  cardGrid: ({ element, section }) =>
    contentfulApiCardGridToSectionElementProps({ cardGrid: element as ContentfulCardGrid, section }),
  listingBlock: ({ element }) =>
    contentfulApiListingBlockToSectionElementProps({
      listingBlock: element as ContentfulListingBlock,
    }),
  productBlock: ({ element }) =>
    contentfulApiProductBlockToSectionElementProps({
      productBlock: element as ContentfulProductBlock,
    }),
  multiColumnBlock: ({ element }) =>
    contentfulApiMultiColumnBlockToSectionElementProps({
      multiColumnBlock: element as ContentfulMultiColumnBlock,
    }),
  sideBySide: ({ element }) =>
    contentfulApiSideBySideToSectionElementProps({ sideBySide: element as ContentfulSideBySide }),
  productGrid: ({ element }) =>
    contentfulApiProductGridToSectionElementProps({ productGrid: element as ContentfulProductGrid }),
};

const getSectionElementProps = ({
  element,
  section,
}: {
  element:
    | ContentfulCardGrid
    | ContentfulCarousel
    | ContentfulHeroBlock
    | ContentfulContentBanner
    | ContentfulListingBlock
    | ContentfulProductBlock
    | ContentfulMultiColumnBlock
    | ContentfulSideBySide
    | ContentfulProductGrid;
  section: ContentfulSection;
}): SectionElementProps<any> | undefined => {
  const typeId = element.sys?.contentType?.sys?.id ?? '';
  const handler = SECTION_ELEMENT_CONFIG[typeId];
  return handler ? handler({ element, section }) : undefined;
};

const contentfulApiCarouselToSectionElementProps = (
  carousel: ContentfulCarousel
): SectionElementProps<any> | undefined => {
  if (!carousel?.fields) return undefined;
  let props: any;
  try {
    props = determineCarouselTransformer<CarouselTransformer>(carousel)?.transformer?.(carousel);
  } catch (err) {
    props = {};
  }
  switch (carousel.fields?.type) {
    case ContentfulCarouselType.Video:
      return { type: ElementType.presentationVideoCarousel, props };
    case ContentfulCarouselType.Merch:
    case ContentfulCarouselType.Chart:
      return { type: ElementType.presentationProductCarousel, props };
    case ContentfulCarouselType.MiniProduct:
      return {
        type: ElementType.productCarousel,
        props: {
          ...props,
          heading: undefined,
          ctaButton: undefined,
        },
      };
    case ContentfulCarouselType.Product:
      return { type: ElementType.carouselLeadImage, props };
    case ContentfulCarouselType.PromoCard:
      return {
        type: ElementType.promoCardCarousel,
        props,
      };
    default:
      return undefined;
  }
};

const contentfulApiHeroBlockToSectionElementProps = (
  heroBlock: ContentfulHeroBlock
): SectionElementProps<any> | undefined => {
  if (!heroBlock?.fields) return undefined;
  return {
    type: ElementType.heroBlock,
    props: HeroBlockTransformer.toHeroBlockProps(heroBlock, { showTags: false }),
  };
};

const contentfulApiContentBannerToSectionElementProps = (
  contentBanner: ContentfulContentBanner
): SectionElementProps<any> | undefined => {
  if (!contentBanner?.fields) return undefined;
  return {
    type: ElementType.contentBanner,
    props: ContentBannerTransformer.toContentBannerProps(contentBanner, {}),
  };
};

const getCardGridParentPageHref = (cardGrid: ContentfulCardGrid): string | undefined => {
  return cardGrid?.fields
    ? cardGrid?.fields?.parentPage?.sys?.contentType?.sys?.id === 'subLandingPage'
      ? `/blogs/${
          (cardGrid?.fields?.parentPage as ContentfulSubCategoryPage)?.fields?.categoryPage?.fields?.urlSlug ?? ''
        }/${cardGrid?.fields?.parentPage?.fields?.urlSlug}`
      : `/blogs/${cardGrid?.fields?.parentPage?.fields?.urlSlug}`
    : undefined;
};

const contentfulApiCardGridToSectionElementProps = ({
  cardGrid,
  section,
}: {
  cardGrid: ContentfulCardGrid;
  section: ContentfulSection;
}): SectionElementProps<any> | undefined => {
  if (!cardGrid?.fields) return undefined;
  return {
    type: ElementType.cardProvider,
    props: contentfulApiCardGridToCardGridWithProviderProps(cardGrid, { featuredBannerCardIndex: 5, section }),
  };
};

const contentfulApiListingBlockToSectionElementProps = ({
  listingBlock,
  isSection = false,
}: {
  listingBlock: ContentfulListingBlock;
  isSection?: boolean;
}): SectionElementProps<any> | undefined => {
  if (!listingBlock?.fields) return undefined;
  return {
    type: ElementType.listingBlock,
    props: {
      ...ListingBlockTransformer.toListingBlockProps(listingBlock),
      ...(isSection ? { heading: '' } : {}),
    },
  };
};

const contentfulApiProductBlockToSectionElementProps = ({
  productBlock,
  isSection = false,
}: {
  productBlock: ContentfulProductBlock;
  isSection?: boolean;
}): SectionElementProps<any> | undefined => {
  if (!productBlock?.fields) return undefined;
  return {
    type: ElementType.productBlock,
    props: {
      ...ProductBlockTransformer.toTabsBlockProps(productBlock),
      ...(isSection ? { heading: '' } : {}),
    },
  };
};

const contentfulApiMultiColumnBlockToSectionElementProps = ({
  multiColumnBlock,
  isSection = false,
}: {
  multiColumnBlock: ContentfulMultiColumnBlock;
  isSection?: boolean;
}): SectionElementProps<any> | undefined => {
  if (!multiColumnBlock?.fields) return undefined;
  return {
    type: ElementType.multiColumnBlock,
    props: {
      ...MultiColumnBlockTransformer.toMultiColumnBlockProps(multiColumnBlock),
      ...(isSection ? { heading: '' } : {}),
    },
  };
};

const contentfulApiSideBySideToSectionElementProps = ({
  sideBySide,
  isSection = false,
}: {
  sideBySide: ContentfulSideBySide;
  isSection?: boolean;
}): SectionElementProps<any> | undefined => {
  if (!sideBySide?.fields) return undefined;
  return {
    type: ElementType.sideBySide,
    props: {
      ...SideBySideTransformer.toSideBySideProps(sideBySide),
      ...(isSection ? { heading: '' } : {}),
    },
  };
};
const contentfulApiProductGridToSectionElementProps = ({
  productGrid,
}: {
  productGrid: ContentfulProductGrid;
}): SectionElementProps<any> | undefined => {
  if (!productGrid?.fields) return undefined;
  return {
    type: ElementType.productGrid,
    props: {
      ...ProductGridTransformer.toProductGridProps(productGrid),
    },
  };
};
